import { useLocation, useNavigate } from 'react-router-dom'

import { PlusCircleIcon, X } from 'lucide-react'
import { cn } from '~/shadcn/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/shadcn/ui/dropdown-menu'
import { FeeImmunityIcon } from '~/@mid_ui/icons/FeeImmunityIcon'
import { MenuIcon } from '~/@mid_ui/icons/MenuIcon'
import { GraphIcon2 } from '~/@mid_ui/icons/GraphIcon2'
import { useSettings } from '~/@mid_settings/settings'
import { ContactSupportIcon } from '~/@mid_ui/icons/ContactSupportIcon'
import { ProjectsIcon } from '~/@mid_ui/icons/ProjectsIcon'
import { LogoutButton } from '../logout-button/LogoutButton'
import { useState } from 'react'

type Props = {
  accountName: React.ReactNode
  isProfileLinkVisible: boolean
  showFeeImmunityLink: boolean
  colorScheme?: 'light' | 'dark'
  showAllItems?: boolean
}

function MobileNavigationMenu({
  accountName,
  isProfileLinkVisible,
  showFeeImmunityLink,
  colorScheme = 'dark',
  showAllItems = true,
}: Props) {
  const { getLandingPageLink } = useSettings()
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)

  function getActiveClassName(checkedPathname: string) {
    return location.pathname === checkedPathname
      ? 'text-accent'
      : 'text-primary-foreground'
  }

  const isDark = colorScheme === 'dark'

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex cursor-pointer focus:outline-none p-4 items-center justify-center',
          'flex h-full self-stretch items-center relative focus-visible:shadow-none focus-visible:outline-none focus-visible:ring-0',
          'after:content-[""] after:block after:w-full after:absolute after:bottom-0 after:left-0 after:h-1 after:z-1 after:bg-accent after:opacity-0 hover:after:opacity-100 after:transition-[opacity,transform] after:duration-300 after:scale-100 after:origin-center'
        )}>
        {isOpen ? (
          <X
            className={isDark ? 'fill-primary' : 'fill-primary-foreground'}
            width={24}
            height={24}
          />
        ) : (
          <MenuIcon
            className={isDark ? 'fill-primary' : 'fill-primary-foreground'}
            width={20}
            height={20}
          />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        className="bg-sidebar/90 backdrop-blur-lg text-white shadow-none p-8">
        {isProfileLinkVisible && (
          <DropdownMenuItem
            onClick={() => {
              navigate('/settings/profile')
            }}
            className={cn('px-0', getActiveClassName('/settings/profile'))}>
            {accountName}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-[#555] mx-0" />

        {showAllItems && (
          <>
            <DropdownMenuItem
              onClick={() => {
                navigate('/projects')
              }}
              className={cn('px-0', getActiveClassName('/projects'))}>
              <ProjectsIcon className="w-5! h-5! text-primary-foreground" />
              Projekty
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => {
                navigate('/account')
              }}
              className={cn('px-0', getActiveClassName('/account'))}>
              <GraphIcon2 className="w-5! h-5! text-primary-foreground" />
              Portfólio
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuItem
          onClick={() => {
            window.open(getLandingPageLink('faq'), '_blank')
          }}
          className={cn('px-0')}>
          <ContactSupportIcon className="w-5! h-5! text-primary-foreground" />
          FAQ
        </DropdownMenuItem>

        {showAllItems && (
          <>
            <DropdownMenuItem
              onClick={() => {
                navigate('/account/add-funds')
              }}
              className={cn('px-0', getActiveClassName('/account/add-funds'))}>
              <PlusCircleIcon className="w-5! h-5! text-primary-foreground" />
              Pridať fin. prostriedky
            </DropdownMenuItem>
          </>
        )}

        {showFeeImmunityLink && (
          <DropdownMenuItem
            onClick={() => {
              navigate('/fee-immunity')
            }}
            className={cn(
              'text-white px-0',
              getActiveClassName('/fee-immunity')
            )}>
            <FeeImmunityIcon />
            Poplatková imunita
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className="bg-[#555] mx-0" />

        <DropdownMenuItem>
          <LogoutButton variant="ghost" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { MobileNavigationMenu }
